import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import PaymentGatewayApi from "../../../../../infrastructure/api/paymentGateway";
import { notifyCustomerFinanceRefresh } from "../../../../components/customer/HeaderFinanceMenu";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import PaymentStatusBadge from "../../../../components/payment-gateway/PaymentStatusBadge";
import PaymentTimeline from "../../../../components/payment-gateway/PaymentTimeline";
import {
  formatRial,
  GATEWAY_SESSION,
  getProviderLabel,
  mapSessionToBadgeState,
  normalizePaymentResult,
  pick,
} from "../../../../components/payment-gateway/paymentGatewayUtils";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const ResultSkeleton = () => (
  <Stack spacing={2}>
    <Skeleton variant="rounded" height={56} />
    <Skeleton variant="rounded" height={120} />
    <Skeleton variant="rounded" height={44} />
  </Stack>
);

const CustomerPaymentResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("sessionId");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const loadResult = useCallback(async () => {
    if (!sessionId) {
      setError("شناسه جلسه پرداخت در آدرس صفحه یافت نشد.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await PaymentGatewayApi.getPaymentResult(sessionId);
      const { error: err, data } = normalizePaymentResult(res);
      if (err) {
        setError(err);
        setResult(null);
      } else {
        setResult(data);
        notifyCustomerFinanceRefresh();
      }
    } catch {
      setError("دریافت نتیجه پرداخت ناموفق بود.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadResult();
  }, [loadResult]);

  const view = useMemo(() => {
    if (!result) return "error";
    if (result.isSuccess || result.IsSuccess) return "success";
    if (result.isPending || result.IsPending) return "pending";
    if (result.isFailed || result.IsFailed) return "failed";
    return "pending";
  }, [result]);

  const invoiceId = pick(result, "invoiceId", "InvoiceId");
  const isBalanceTopUp =
    pick(result, "isBalanceTopUp", "IsBalanceTopUp") === true;
  const balanceAfter = pick(result, "availableBalanceAfter", "AvailableBalanceAfter");
  const sessionStatus = pick(result, "status", "Status");
  const badgeState = mapSessionToBadgeState(sessionStatus);
  const timeline = pick(result, "timeline", "Timeline");
  const isUnknown =
    view === "pending" &&
    (sessionStatus === GATEWAY_SESSION.Unknown || sessionStatus === 8 || sessionStatus === "8");

  const goToInvoicePayment = () => {
    if (invoiceId) {
      navigate(`/SLN/Customer/InvoicePayment/${invoiceId}`);
    } else {
      navigate("/SLN/Customer/Requests");
    }
  };

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={8} lg={6} sx={{ mx: "auto" }}>
        <Card sx={{ borderRadius: 2, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)" }}>
          <CardHeader
            title={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" component="span">
                  نتیجه پرداخت
                </Typography>
                {result ? <PaymentStatusBadge state={badgeState} /> : null}
              </Stack>
            }
            subheader="وضعیت نهایی از سامانه سولارنت"
          />
          <CardContent sx={{ pt: 0 }}>
            {loading ? <ResultSkeleton /> : null}

            {!loading && error ? (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  sessionId ? (
                    <Button color="inherit" size="small" onClick={loadResult}>
                      تلاش مجدد
                    </Button>
                  ) : null
                }
              >
                {error}
              </Alert>
            ) : null}

            {!loading && result ? (
              <Stack spacing={2}>
                {view === "success" ? (
                  <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                    {pick(result, "message", "Message") ||
                      (isBalanceTopUp
                        ? "افزایش اعتبار با موفقیت انجام شد."
                        : "پرداخت با موفقیت انجام شد.")}
                  </Alert>
                ) : null}

                {view === "failed" ? (
                  <Alert severity="error" icon={<ErrorOutlineIcon />}>
                    {pick(result, "message", "Message") || "پرداخت ناموفق بود."}
                  </Alert>
                ) : null}

                {view === "pending" ? (
                  <Alert severity="info" icon={isUnknown ? <HelpOutlineIcon /> : <HourglassEmptyIcon />}>
                    {pick(result, "message", "Message") ||
                      (isUnknown
                        ? "پرداخت شما ممکن است هنوز در حال نهایی‌شدن باشد. چند دقیقه صبر کنید و وضعیت را بروزرسانی کنید."
                        : "در حال بررسی پرداخت — لطفاً چند لحظه دیگر بروزرسانی کنید.")}
                  </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      مبلغ: {formatRial(pick(result, "amount", "Amount"))}
                    </Typography>
                    <Typography variant="body2">
                      درگاه: {getProviderLabel(pick(result, "provider", "Provider"))}
                    </Typography>
                    {pick(result, "providerTrackingCode", "ProviderTrackingCode") ? (
                      <Typography variant="body2">
                        کد پیگیری:{" "}
                        <strong>
                          {pick(result, "providerTrackingCode", "ProviderTrackingCode")}
                        </strong>
                      </Typography>
                    ) : null}
                    {balanceAfter != null && isBalanceTopUp ? (
                      <Typography variant="body2">
                        مانده اعتبار پنل:{" "}
                        <strong>{formatRial(balanceAfter)}</strong>
                      </Typography>
                    ) : null}
                    {pick(result, "paidAt", "PaidAt") ? (
                      <Typography variant="body2">
                        زمان پرداخت: {toJalaliFullDate(pick(result, "paidAt", "PaidAt"))}
                      </Typography>
                    ) : null}
                  </Stack>
                  <PaymentTimeline timeline={timeline} />
                </Paper>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={loadResult}
                    disabled={loading}
                  >
                    بروزرسانی وضعیت
                  </Button>
                  {view === "success" && isBalanceTopUp ? (
                    <>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate("/SLN/Customer/Balance/History")}
                      >
                        مشاهده اعتبار پنل
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate("/SLN/Customer")}
                      >
                        بازگشت به داشبورد
                      </Button>
                    </>
                  ) : null}
                  {view === "failed" && invoiceId && !isBalanceTopUp ? (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ReplayIcon />}
                      onClick={goToInvoicePayment}
                    >
                      تلاش مجدد
                    </Button>
                  ) : null}
                  {!isBalanceTopUp || view !== "success" ? (
                    <Button
                      fullWidth
                      variant="text"
                      startIcon={<UndoIcon />}
                      onClick={() => navigate("/SLN/Customer/Requests")}
                    >
                      بازگشت
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            ) : null}

            {!loading && !result && !error ? (
              <Box py={2}>
                <Typography variant="body2" color="text.secondary" align="center">
                  اطلاعاتی برای نمایش وجود ندارد.
                </Typography>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CustomerPaymentResultPage;
